'use strict';

import moment from 'moment-timezone';
import _ from 'lodash';

import * as enums from '@playbook-technologies/common-enums';

const nanValue = 0;
const alpha = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
const ytLinkRegex =
  /^(?:https?:\/\/)?(?:www\.)?(?:youtu\.be\/|youtube\.com\/(?:embed\/|v\/|watch\?v=|watch\?.+&v=))((\w|-){11})(?:\S+)?$/;

function validateYTURI(url) {
  const matches = url.match(ytLinkRegex);
  if (matches) {
    return true;
  }
  return false;
}

function shortenString(string, maxLength) {
  if (string && string.length > maxLength) {
    string = string.substring(0, maxLength - 1) + '…';
  }
  return string;
}

function getRandomHex() {
  return Math.random().toString(16).slice(2, 7);
}

function tryParseInt(str, defaultValue) {
  if (str !== null && str.length > 0 && !isNaN(str)) {
    return parseInt(str);
  }
  return defaultValue;
}

function tryParseIntThrow(str) {
  if (!isNaN(str)) {
    return parseInt(str);
  }
  throw new Error('Not an integer.');
}

function dateToStringSimple(date) {
  let str = moment(date).format('DD MMM YYYY');
  return str;
}

function dateTimeToString(iso8601, timeZone = 'Europe/Berlin') {
  let str = moment.tz(iso8601, timeZone).format('DD MMM YYYY h:mm a');
  return str;
}

function dateTimeToStringTimezone(iso8601, timeZone = 'Europe/Berlin') {
  let str = moment.tz(iso8601, timeZone).format('DD MMM YYYY h:mm a (z)');
  return str;
}

function dateTimeToStringFriendly(iso8601, timeZone = 'Europe/Berlin') {
  let parsed = moment.tz(iso8601, timeZone);
  let currentYear = moment().year();
  if (parsed.year() === currentYear) {
    return parsed.format('MMM D');
  } else {
    return parsed.format('MMM D YYYY');
  }
}

function indexToString(index, subIndex) {
  let str = index + 1;
  if (subIndex === undefined) return str;
  else if (subIndex > -1 && subIndex < 26) return str + alpha[subIndex];
  else return str + '∞';
}

function getYouTubeId(url) {
  if (!url || !url.indexOf) return null;

  let parts = url.split('v=');
  // youtube format with 'v' attribute
  if (parts[1]) {
    let idPart = parts[1];
    let ampPosition = idPart.indexOf('&');
    if (ampPosition !== -1) idPart = idPart.substring(0, ampPosition);
    return idPart;
  }
  // short youtube format
  parts = url.split('youtu.be/');
  if (parts[1]) {
    let idPart = parts[1];
    let qPosition = idPart.indexOf('?');
    if (qPosition !== -1) idPart = idPart.substring(0, qPosition);
    return idPart;
  }

  return null;
}

function prepareExercise(e) {
  // trim name & description if necessary
  if (e.name) {
    e.name = e.name.trim();
  }
  if (e.description) {
    e.description = e.description.trim();
  }

  if (e.type === enums.Exercise.Type.YouTube.value) {
    _.each(e.exercises, (e) => {
      console.log(e);
    });
  }

  // parse non-string form fields to native types
  if (e.type === enums.Exercise.Type.Custom.value) {
    console.error('Trying to prepare a Custom exercise!', e);
  } else if (e.type === enums.Exercise.Type.Single.value) {
    // single rest
    if (e.rest === null || e.rest === '') {
      e.rest = null;
    } else {
      e.rest = parseInt(e.rest);
      if (isNaN(e.rest)) e.rest = nanValue;
    }
    // single sets
    _.each(e.sets, (set) => {
      // single set reps
      if (set.reps[0] === null || set.reps[0] === '') {
        set.reps = [null, set.reps[1]];
      } else {
        set.reps = [parseInt(set.reps[0]), set.reps[1]];
        if (isNaN(set.reps[0])) set.reps[0] = nanValue;
      }
    });
  } else if (e.type === enums.Exercise.Type.Multiple.value) {
    // multiple duration
    if (e.duration[0] === null || e.duration[0] === '') {
      e.duration = [null, e.duration[1]];
    } else {
      e.duration = [parseInt(e.duration[0]), e.duration[1]];
      if (isNaN(e.duration[0])) {
        e.duration = [nanValue, e.duration[1]];
      }
    }
    // multiple rest
    if (e.rest === null || e.rest === '') {
      e.rest = null;
    } else {
      e.rest = parseInt(e.rest);
      if (isNaN(e.rest)) {
        e.rest = nanValue;
      }
    }
    // multiple exercises
    if (e.exercises && e.exercises.length > 0) {
      _.each(e.exercises, (ee) => {
        // ACHTUNG recursion
        ee = prepareExercise(ee);
      });
    }
  }
  return e;
}

function generateExercise(type, durationUnit) {
  if (type == enums.Exercise.Type.Multiple.value)
    return generateMultipleExercise(durationUnit);
  else if (type == enums.Exercise.Type.Single.value)
    return generateSingleExercise();
  else if (type == enums.Exercise.Type.YouTube.value)
    return generateYouTubeExercise();
  else if (type == enums.Exercise.Type.Product.value)
    return generateProductExercise();
  else if (type == enums.Exercise.Type.Nutrition.value)
    return generateNutritionExercise();
  else
    console.error(
      'Attempting to generate unknown or deprecated exercise type.',
      { type, durationUnit },
    );
}

function generateSingleExercise() {
  return {
    type: enums.Exercise.Type.Single.value,
    media: null,
    name: '',
    description: '',
    sets: [generateSingleExerciseItem('min')],
    rest: '',
  };
}

function generateSingleExerciseItem(repUnit = 'reps') {
  return {
    reps: ['', repUnit],
  };
}

function generateMultipleExercise(durationUnit = 'rounds') {
  return {
    type: enums.Exercise.Type.Multiple.value,
    name: '',
    description: '',
    duration: ['', durationUnit],
    rest: '',
    exercises: [],
  };
}

function generateYouTubeExercise() {
  return {
    type: enums.Exercise.Type.YouTube.value,
    name: '',
    description: '',
    youtubeId: '',
  };
}

function generateProductExercise() {
  return {
    type: enums.Exercise.Type.Product.value,
    name: '',
    description: '',
    link: '',
    media: null,
    image: null,
  };
}

function generateNutritionExercise() {
  return {
    type: enums.Exercise.Type.Nutrition.value,
    name: '',
    description: '',
    link: '',
    media: null,
    image: null,
  };
}

function validateString(value, required = true) {
  // don't allow undefined values
  if (value === undefined) {
    return false;
  }
  if (required) {
    // don't allow null values
    if (value === null) {
      return false;
    }
    // type check: don't allow anything that's not a string
    if (typeof value !== 'string') {
      return false;
    }
    value = value.trim();
    // don't allow blank entries
    if (value === '') {
      return false;
    }
    // all checks passed
    return true;
  } else {
    // since string is not required, allow nulls or (empty) strings
    if (value === null || typeof value === 'string') {
      //all checks passed
      return true;
    }
    return false;
  }
}

function validateInt(
  value,
  required = true,
  allowZero = true,
  allowNegative = false,
) {
  // don't allow undefined or NaN values
  if (value === undefined || isNaN(value)) {
    return false;
  }
  // type check: don't allow anything that's not a number
  if (typeof value !== 'number') {
    return false;
  }
  // integers should be integers
  if (value % 1 !== 0) {
    return false;
  }
  if (required) {
    // if zero is not allowed, check
    if (!allowZero && value === 0) {
      return false;
    }
    // if negative numbers are not allowed, check
    if (!allowNegative && value < 0) {
      return false;
    }
  }
  // all checks passed
  return true;
}

function validateFloat(
  value,
  required = true,
  allowZero = true,
  allowNegative = false,
) {
  // don't allow undefined, NaN or Infinity values
  if (value === undefined || isNaN(value) || value === Infinity) {
    return false;
  }
  // type check: don't allow anything that's not a number
  if (typeof value !== 'number') {
    return false;
  }
  if (required) {
    // if zero is not allowed, check
    if (!allowZero && value === 0) {
      return false;
    }
    // if negative numbers are not allowed, check
    if (!allowNegative && value < 0) {
      return false;
    }
  }
  // all checks passed
  return true;
}

function validateObject(value, required = true, allowBlank = false) {
  // don't allow undefined values
  if (value === undefined) {
    return false;
  }
  if (typeof value === 'object') {
    // don't allow arrays
    if (value && value.constructor === Array) {
      return false;
    }
    if (required) {
      // if value is required, don't allow nulls
      if (value === null) {
        return false;
      }
      // if we don't allow blank objects, check keys
      if (!allowBlank && Object.keys(value).length === 0) {
        return false;
      } else {
        return true;
      }
    } else {
      return true;
    }
  } else {
    // certainly not an object (or null, because typeof null === 'object')
    return false;
  }
}

/**
 * Validate ISO8601 (string) date.
 * @param {string} value ISO8601 formatted string.
 * @param {bool} required Require non-null value.
 */
function validateDate(value, required = true) {
  //console.log('strignes li ti ovdje', value);
  if (value === undefined) {
    console.log('date undefined');
    return false;
  }
  if (typeof value === 'string') {
    // if not required & null, return true
    if (!required && value === null) {
      //console.log('date not required and null');
      return true;
    }
    var t = moment(value).isValid();
    //console.log('is date valid?', t);
    return t;
  } else {
    // not a string, so not a ISO8601 string either
    return false;
  }
}

function isTreeHealthy(node) {
  if (node === null || node === false) {
    //console.log('Node is null or false.', node);
    return true;
  } else if (typeof node === 'string') {
    //console.log('Node is a string.', node);
    return node.length > 0 ? false : true;
  } else if (node.constructor === Array) {
    //console.log('Node is an Array.', node);
    for (let i = 0; i < node.length; i++) {
      if (!isTreeHealthy(node[i])) {
        return false;
      }
    }
    return true;
  } else if (typeof node === 'object') {
    //console.log('Node is an object.', node);
    let keys = Object.keys(node);
    for (let i = 0; i < keys.length; i++) {
      if (!isTreeHealthy(node[keys[i]])) {
        return false;
      }
    }
    return true;
  } else {
    console.log('Node is of an unknown type.', node);
    throw new Error('Node is of an unknown type.');
  }
}

function getValidationMessages(tree) {
  let messages = [];
  if (!tree) return messages;
  // TODO this flattens the validation tree into an array of validation messages, if there are any
  // my guess is this is recursive
  // remember validation tree properties: at it's root it's an object with keys
  // strings are validation messages, arrays contain objects or validation messages - that's the struct we use for exercises
}

function getInitials(name) {
  if (!name) return '';
  return name
    .split(' ')
    .map((w) => w.toUpperCase()[0])
    .join('');
}

function getRatingStars(floatValue) {
  let result = '';
  for (let i = 0; i < floatValue; i++) {
    result += '★';
  }
  return result;
}

function getRandomColor() {
  return '#' + Math.floor(Math.random() * 16777215).toString(16);
}

const exports = {
  shortenString,
  getRandomHex,
  tryParseInt,
  tryParseIntThrow,
  dateTimeToString,
  dateTimeToStringTimezone,
  dateTimeToStringFriendly,
  dateToStringSimple,
  indexToString,
  getYouTubeId,
  prepareExercise,
  generateExercise,
  generateSingleExercise,
  generateSingleExerciseItem,
  generateMultipleExercise,
  generateYouTubeExercise,
  generateProductExercise,
  generateNutritionExercise,
  validateString,
  validateInt,
  validateFloat,
  validateObject,
  validateDate,
  isTreeHealthy,
  getValidationMessages,
  validateYTURI,
  getInitials,
  getRatingStars,
  getRandomColor,
};

export default exports;
export {
  // NOTE: duplicate of exports, hacked to make it work
  shortenString,
  getRandomHex,
  tryParseInt,
  tryParseIntThrow,
  dateTimeToString,
  dateTimeToStringTimezone,
  dateTimeToStringFriendly,
  dateToStringSimple,
  indexToString,
  getYouTubeId,
  prepareExercise,
  generateExercise,
  generateSingleExercise,
  generateSingleExerciseItem,
  generateMultipleExercise,
  generateYouTubeExercise,
  generateProductExercise,
  generateNutritionExercise,
  validateString,
  validateInt,
  validateFloat,
  validateObject,
  validateDate,
  isTreeHealthy,
  getValidationMessages,
  validateYTURI,
  getInitials,
  getRatingStars,
  getRandomColor,
};
